/**
 * One yarn, as a shop would put it on a shelf.
 *
 * The drawings at the top are the reason this page exists in the state it does: a
 * length of the yarn itself, and a square of the fabric it knits at its own gauge.
 * Both come out of the catalogue figures printed underneath them, so the picture and
 * the numbers cannot disagree.
 *
 * ### What the card says, and in what order
 *
 * A knitter reads a shelf label in a fixed order, and this follows it. What it looks
 * like, who spins it and what it is called, what it costs, then the measurements, then
 * what other people thought. The price sits above the specification block rather than
 * buried in it because it is the second question anybody asks, and the per hundred
 * yards figure sits next to it because a fifty gram skein and a two hundred and fifty
 * gram cone cannot otherwise be compared.
 *
 * ### The card is not a link
 *
 * There is no detail page to go to. Everything the catalogue holds about a yarn is on
 * the card already, including the notes, so making it clickable would promise
 * something that does not exist. The hover state lifts the card and warms the swatch
 * because a shelf should feel touchable, and it does nothing else.
 *
 * ### The stagger
 *
 * Cards arrive in sequence rather than all at once, which is the difference between a
 * page appearing and a page being laid out in front of you. The delay is capped, so a
 * catalogue three times this size would not make the last card wait a second and a
 * half, and the whole thing is governed by the reduced motion setting through the
 * `MotionConfig` in `App.tsx`. `layout` is on the article so that filtering slides the
 * survivors into their new places rather than snapping them.
 */

import { motion } from 'framer-motion';

import { gaugeRangeText, substitutionCandidates, type YarnRecord } from '../catalogue';
import {
  centsPer100Yards,
  characterText,
  fibreCharacter,
  priceText,
  shelfEntry,
  yardsPerGram,
} from '../shop';

import { GaugeBand } from './GaugeBand';
import { Notes } from './Notes';
import { Skein } from './Skein';
import { StitchSwatch } from './StitchSwatch';
import { weightLabel } from './BrowseBar';

/** How many substitutes a card names. Three is a sentence, six is a list. */
const CANDIDATES_SHOWN = 3;

/** Substitutes as a sentence, since three names do not earn a list. */
const substitutesLine = (yarn: YarnRecord): string => {
  const names = substitutionCandidates(yarn.id, CANDIDATES_SHOWN).map(
    (candidate) => candidate.name,
  );
  return names.length === 0 ? 'Nothing else on file.' : names.join(', ');
};

const STAGGER_STEP = 0.055;
const STAGGER_CAP = 8;

/**
 * How long the card at this position waits before it arrives, in seconds.
 *
 * Capped, and the cap is the whole reason this is a function. A stagger that is
 * position times a step reads beautifully for seven cards and is a bug for seventy:
 * the last card would sit blank for four seconds while a reader stared at it. Eight
 * steps is a little under half a second, which is as long as a page is allowed to
 * spend laying itself out.
 */
export const arrivalDelay = (index: number): number =>
  Math.min(Math.max(0, index), STAGGER_CAP) * STAGGER_STEP;

export interface YarnCardProps {
  readonly yarn: YarnRecord;
  /** Position in the current result set, which sets the arrival delay. */
  readonly index: number;
}

export const YarnCard = ({ yarn, index }: YarnCardProps): JSX.Element => {
  const entry = shelfEntry(yarn.id);
  const character = fibreCharacter(yarn.fibre);
  const perHundred = centsPer100Yards(yarn, entry);

  return (
    <motion.article
      animate={{ opacity: 1, y: 0 }}
      aria-labelledby={`yarn-${yarn.id}`}
      className="card raised"
      data-dye={entry.dye}
      data-handle={character}
      data-yarn={yarn.id}
      exit={{ opacity: 0, y: -8 }}
      initial={{ opacity: 0, y: 18 }}
      layout
      transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1], delay: arrivalDelay(index) }}
    >
      <div className="card__swatches">
        <Skein yarn={yarn} />

        <figure className="card__fabric">
          <StitchSwatch yarn={yarn} />
          <figcaption className="card__fabric-note num">2 in</figcaption>
        </figure>

        <p className="card__colourway">
          <span aria-hidden="true" className="card__dot" />
          {entry.colourway}
        </p>
      </div>

      <div className="card__body">
        <p className="eyebrow card__mill">{entry.mill}</p>
        <h3 className="card__name" id={`yarn-${yarn.id}`}>
          {yarn.name}
        </h3>
        <p className="card__line">
          <span className="card__weight">{weightLabel(yarn.weight)}</span>
          <span aria-hidden="true" className="card__sep" />
          <span className="num">{entry.plies} ply</span>
          <span aria-hidden="true" className="card__sep" />
          <span className="num">{yardsPerGram(yarn)} yd a gram</span>
        </p>

        <p className="card__price">
          <span className="card__price-figure num">{priceText(entry.centsPerSkein)}</span>
          <span className="card__price-unit">a skein</span>
          <span className="card__price-rate num">{priceText(perHundred)} / 100 yd</span>
        </p>

        <dl className="spec">
          <dt className="spec__key">Ball band</dt>
          <dd className="spec__value num">{gaugeRangeText(yarn)}</dd>

          <dt className="spec__key">Our swatch</dt>
          <dd className="spec__value num">
            {yarn.stsPer4in} sts and {yarn.rowsPer4in} rows / 4 in
          </dd>

          <dt className="spec__key">Skein</dt>
          <dd className="spec__value num">
            {yarn.yardsPerSkein} yd, {yarn.gramsPerSkein} g
          </dd>

          <dt className="spec__key">Fibre</dt>
          <dd className="spec__value">{yarn.fibre}</dd>

          <dt className="spec__key">Substitutes</dt>
          <dd className="spec__value">{substitutesLine(yarn)}</dd>
        </dl>

        {/*
          The two gauge rows in the block above, drawn as one reading. The numbers stay
          in the list because the list is the record; this says what they amount to.
        */}
        <GaugeBand yarn={yarn} />

        <p className="card__handle">{characterText(character)}</p>

        <Notes yarn={yarn} />
      </div>
    </motion.article>
  );
};
